import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WEBSOCKET } from '../environment';
import { setDchatStream } from 'slices/dchatSlice';
import { useDispatch, useStore } from 'react-redux';
import type { Store } from 'redux';
interface WebsocketContextType {
  needsRefresh: boolean;
  isConnected: boolean;
}

const WebsocketContext = createContext<WebsocketContextType | undefined>(
  undefined
);

const validEndpoints = {
  '/dice/editgroup': ['groups'],
  '/dice/deleteGroup': ['groups'],
  '/dice/inviteTogroup': ['groups', 'users'],
  '/dice/removeUserFromGroup': ['groups', 'users'],
  'dice/roles/createRoleWithPermissions': ['groups', 'roles'],
  'dice/roles/unAssignUserFromRole': ['groups', 'roles', 'users'],
  '/dice/roles/assignUserToRole': ['groups', 'roles', 'users'],
  '/dice/roles/editRoleAndPermissions': ['groups', 'roles'],
};

export const WebsocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const store: Store = useStore();

  useEffect(() => {
    const comsWebSocket = WEBSOCKET;
    let client: WebSocket | null = null;
    let retries = 0;
    const maxRetries = 4;
    const retryDelay = 3000;

    const connectWebSocket = () => {
      try {
        client = new WebSocket(comsWebSocket);
      } catch (error) {
        console.error('Error creating WebSocket client', error);
        return;
      }

      client.onopen = () => {
        // Set connection state in React Query cache
        setIsConnected(true);
        retries = 0;
      };

      client.onmessage = (message) => {
        if (message && message.data) {
          const payload = JSON.parse(message.data);
          if (payload.endpoint) {
            const currStore = store.getState();
            const userId = currStore.app.user.user_guid;
            const groups = currStore.app.groups;
            if (Array.isArray(groups) && payload.group) {
              const implicatedGroup = groups.find(
                (group: Group) => group.group_guid === payload.group
              );

              if (
                implicatedGroup &&
                userId &&
                userId !== payload.requesting_user
              ) {
                invalidateRelatedQueries(payload.endpoint);
                setNeedsRefresh(true);
              }
            }
          }
          if (payload.streamtext) {
            dispatch(setDchatStream(payload.streamtext));
          }
        }
      };

      client.onerror = () => {
        console.error('WebSocket encountered an error.');
        setIsConnected(false);
      };

      client.onclose = () => {
        setIsConnected(false);

        if (retries < maxRetries) {
          setTimeout(() => {
            retries += 1;
            console.warn(
              `WebSocket connection lost. Retrying... (${retries}/${maxRetries})`
            );
            connectWebSocket();
          }, retryDelay);
        } else {
          console.error('Failed to reconnect after 4 attempts.');
        }
      };
    };

    connectWebSocket();

    const stayAliveInterval = setInterval(() => {
      if (client && client.readyState === WebSocket.OPEN) {
        client.send('stay alive');
      }
    }, 40000);

    return () => {
      clearInterval(stayAliveInterval);
      if (client) {
        client.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invalidateRelatedQueries = (endpoint: string) => {
    const queriesToInvalidate =
      validEndpoints[endpoint as keyof typeof validEndpoints];
    if (queriesToInvalidate) {
      // Invalidate each query type associated with this endpoint
      queriesToInvalidate.forEach((queryType) => {
        queryClient.invalidateQueries({ queryKey: [queryType] });
      });
    }
  };

  return (
    <WebsocketContext.Provider
      value={{
        needsRefresh: needsRefresh,
        isConnected: isConnected,
      }}
    >
      {children}
    </WebsocketContext.Provider>
  );
};

export const useWebsocket = () => {
  const context = useContext(WebsocketContext);
  if (!context) {
    throw new Error('useWebsocket must be used within WebsocketProvider');
  }
  return context;
};
